import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout/PageDetailLayout';
import { Button, Card, Badge, Table, EmptyState } from '@/components/ui';
import { useCompanyDetail } from './hooks/useCompanies';
import { PLAN_CONFIG, FEATURE_LABELS } from './types';
import type { SubscriptionPlanCode } from './types';

const TABS = [
  { value: 'overview', label: 'Tổng quan', icon: 'ri-information-line' },
  { value: 'projects', label: 'Dự án', icon: 'ri-building-2-line' },
  { value: 'users', label: 'Nhân sự', icon: 'ri-team-line' },
  { value: 'subscription', label: 'Gói sử dụng', icon: 'ri-vip-crown-line' },
  { value: 'documents', label: 'Tài liệu', icon: 'ri-file-list-3-line' },
  { value: 'history', label: 'Lịch sử', icon: 'ri-history-line' },
];

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const { company, usage, subscriptions, projects, users, isLoading, error } = useCompanyDetail(id);

  if (!id) return <EmptyState title="Không tìm thấy ID công ty" />;

  const planCfg = company ? (PLAN_CONFIG[company.subscription_plan_code as SubscriptionPlanCode] || PLAN_CONFIG.free) : PLAN_CONFIG.free;

  return (
    <PageDetailLayout
      title={company?.name || 'Chi tiết công ty'}
      description={company?.code || 'Tenant ID'}
      breadcrumbs={[
        { label: 'Hệ thống', path: '/admin' },
        { label: 'Công ty', path: '/companies' },
        { label: company?.name || 'Chi tiết' },
      ]}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isLoading={isLoading}
      error={error?.message}
      headerActions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon="ri-edit-line" onClick={() => navigate(`/companies/edit/${id}`)}>
            Chỉnh sửa
          </Button>
          <Button variant="primary" icon="ri-share-line">
            Chia sẻ
          </Button>
        </div>
      }
      image={
        <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center border border-green-100">
          <i className="ri-building-4-line text-green-600 text-3xl" />
        </div>
      }
    >
      {activeTab === 'overview' && company && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 md:col-span-2">
              <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                <i className="ri-building-line text-green-600" />
                Thông tin cơ bản
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12">
                {[
                  { label: 'Tên đầy đủ', value: company.name },
                  { label: 'Mã định danh', value: company.code, mono: true },
                  { label: 'Email liên hệ', value: company.email || 'Chưa cập nhật' },
                  { label: 'Số điện thoại', value: company.phone || 'Chưa cập nhật' },
                  { label: 'Website', value: company.website || 'Chưa cập nhật', link: true },
                  { label: 'Địa chỉ', value: company.address || 'Chưa cập nhật' },
                  { label: 'Thành phố', value: company.city || 'Chưa cập nhật' },
                  { label: 'Ngày gia nhập', value: new Date(company.created_at).toLocaleDateString('vi-VN') },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-bold text-slate-400  tracking-widest mb-1">{item.label}</p>
                    <p className={`text-sm font-semibold text-slate-800 ${item.mono ? 'font-mono' : ''}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-slate-50">
                <p className="text-[10px] font-bold text-slate-400  tracking-widest mb-2">Mô tả tổ chức</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {company.description || 'Chưa có thông tin giới thiệu cho công ty này.'}
                </p>
              </div>
            </Card>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-sm font-bold text-slate-900 mb-4  tracking-wider">Hợp đồng & Gói</h3>
                <div className={`p-4 rounded-2xl ${planCfg.bg} ${planCfg.border} border mb-4`}>
                  <p className={`text-[10px] font-bold  tracking-widest ${planCfg.text} mb-1`}>Gói hiện tại</p>
                  <h4 className={`text-xl font-black ${planCfg.text}`}>{planCfg.label} Edition</h4>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium">Trạng thái</span>
                    <Badge variant={company.is_active ? 'success' : 'warning'}>
                      {company.is_active ? 'Đang hoạt động' : 'Chờ duyệt'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium">Ngày hết hạn</span>
                    <span className="text-slate-800 font-bold">
                      {company.subscription_end_date ? new Date(company.subscription_end_date).toLocaleDateString('vi-VN') : 'Vô thời hạn'}
                    </span>
                  </div>
                </div>
                <Button variant="secondary" className="mt-6 w-full" icon="ri-arrow-up-circle-line">
                  Nâng cấp dịch vụ
                </Button>
              </Card>

              <Card className="p-6">
                <h3 className="text-sm font-bold text-slate-900 mb-4  tracking-wider">Hạn mức tài nguyên</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Dự án', current: usage?.projects_count || 0, max: company.max_projects, icon: 'ri-building-2-line', color: 'blue' },
                    { label: 'Nhân sự', current: usage?.users_count || 0, max: company.max_users, icon: 'ri-team-line', color: 'green' },
                    { label: 'Tài sản', current: usage?.assets_count || 0, max: company.max_assets, icon: 'ri-leaf-line', color: 'emerald' },
                  ].map(item => {
                    const isUnlimited = item.max < 0;
                    const pct = isUnlimited ? 0 : Math.min(100, Math.round((item.current / item.max) * 100));
                    return (
                      <div key={item.label} className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500 font-medium flex items-center gap-1">
                            <i className={item.icon} />
                            {item.label}
                          </span>
                          <span className="text-slate-900 font-bold">
                            {item.current.toLocaleString()} / {isUnlimited ? '∞' : item.max.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${isUnlimited ? 'bg-slate-200' : pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${isUnlimited ? 0 : pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-2xl">
                    <p className="text-[10px] font-bold text-slate-400  tracking-widest mb-1">Lệnh làm việc</p>
                    <p className="text-lg font-black text-slate-800">{(usage?.work_orders_count || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-2xl">
                    <p className="text-[10px] font-bold text-slate-400  tracking-widest mb-1">Nhật ký</p>
                    <p className="text-lg font-black text-slate-800">{(usage?.logs_count || 0).toLocaleString()}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'projects' && (
        <Card className="p-0 overflow-hidden">
          <Table
            data={projects}
            columns={[
              { key: 'code', label: 'Mã dự án', render: (v) => <span className="font-mono text-xs font-bold">{v}</span> },
              { key: 'name', label: 'Tên dự án', render: (v) => <span className="font-bold text-slate-800">{v}</span> },
              { key: 'status_code', label: 'Trạng thái', render: (v) => <Badge variant={v === 'active' ? 'success' : 'default'}>{v}</Badge> },
              { key: 'created_at', label: 'Ngày tạo', render: (v) => new Date(v).toLocaleDateString('vi-VN') },
            ]}
            onRowClick={(row) => navigate(`/projects/detail/${row.id}`)}
          />
        </Card>
      )}

      {activeTab === 'users' && (
        <Card className="p-0 overflow-hidden">
          <Table
            data={users}
            columns={[
              {
                key: 'full_name', label: 'Họ tên', render: (v, row) => (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                      {v?.charAt(0) || row.email?.charAt(0)}
                    </div>
                    <span className="font-bold text-slate-800">{v || 'N/A'}</span>
                  </div>
                )
              },
              { key: 'email', label: 'Email', render: (v) => <span className="text-slate-500">{v}</span> },
              { key: 'role_code', label: 'Vai trò', render: (v) => <Badge variant="secondary">{v}</Badge> },
              { key: 'is_active', label: 'Trạng thái', render: (v) => <Badge variant={v ? 'success' : 'danger'}>{v ? 'Active' : 'Locked'}</Badge> },
            ]}
          />
        </Card>
      )}

      {activeTab === 'subscription' && (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Lịch sử đăng ký & Thanh toán</h3>
            <Table
              data={subscriptions}
              columns={[
                { key: 'start_date', label: 'Ngày bắt đầu', render: (v) => new Date(v).toLocaleDateString('vi-VN') },
                { key: 'plan_code', label: 'Gói dịch vụ', render: (v) => <Badge className={PLAN_CONFIG[v as SubscriptionPlanCode]?.badge}>{v}</Badge> },
                { key: 'billing_cycle', label: 'Chu kỳ', render: (v) => <span className=" text-xs font-bold">{v}</span> },
                { key: 'amount_paid', label: 'Số tiền', render: (v) => <span className="font-bold text-slate-800">{v.toLocaleString('vi-VN')}đ</span> },
                { key: 'status_code', label: 'Trạng thái', render: (v) => <Badge variant={v === 'active' ? 'success' : 'default'}>{v}</Badge> },
              ]}
            />
          </Card>
        </div>
      )}

      {['documents', 'history'].includes(activeTab) && (
        <Card className="p-12 flex flex-col items-center justify-center text-center">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
            <i className="ri-construction-line text-slate-300 text-4xl" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Tính năng đang phát triển</h3>
          <p className="text-sm text-slate-500 max-w-sm">
            Phân hệ này đang trong giai đoạn triển khai Sprint P5. Vui lòng quay lại sau.
          </p>
        </Card>
      )}
    </PageDetailLayout>
  );
}
